import { FormHelperText } from "@chakra-ui/form-control";
import {
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Spacer,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { Form, Formik, FormikHelpers, useFormikContext } from "formik";
import { InputControl } from "formik-chakra-ui";
import _ from "lodash";
import React from "react";
import {
  RiAddCircleLine,
  RiDeleteBin6Line,
  RiQuestionAnswerLine,
  RiShieldLine,
} from "react-icons/ri";
import { ConnectionTag } from "src/components/ConnectionTag";
import { PhoneInput } from "src/components/Inputs/PhoneInput";
import {
  AdminCard,
  AdminCardFormButtons,
} from "src/components/Layout/AdminCard";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import {
  Factor,
  NeedReauthenticateError,
  useMfaApi,
} from "src/hooks/useMfaApi";
import { formatUSPhoneNumber, maskedPhoneNumber } from "src/services/format";
import { validateWithZod } from "src/services/formValidations";
import { isNotNull, isNullOrUndefined } from "src/services/predicates";
import { z } from "zod";

type Props = {
  factors: Factor[];
  refreshFactors: () => Promise<void>;
};

export const PhoneFactorCard: React.FC<Props> = ({
  factors,
  refreshFactors,
}) => {
  const { onOpen, onClose, isOpen } = useDisclosure();
  const mfaApi = useMfaApi();
  const toast = useAvelaToast();
  const [state, setState] = React.useState<"view" | "new">("view");

  const isActive = React.useMemo(() => {
    return !isNullOrUndefined(factors.find((f) => f.active));
  }, [factors]);

  const onSubmit = React.useCallback(
    async (values: FormType, helpers: FormikHelpers<FormType>) => {
      switch (values.step) {
        case "phone": {
          try {
            const phoneEnrollment = await mfaApi.enrollPhone(
              values.phoneNumber
            );
            await helpers.setValues((values) => ({
              ...values,
              step: "code",
              oobCode: phoneEnrollment.oob_code,
            }));
            helpers.setFieldError("verificationCode", undefined);
          } catch (error) {
            if (error instanceof NeedReauthenticateError) {
              window.location.reload();
            } else {
              console.error(error);
              toast.genericError();
            }
          }
          break;
        }
        case "code": {
          try {
            await mfaApi.confirmPhone({
              verificationCode: values.verificationCode,
              oobCode: values.oobCode,
            });
            await refreshFactors();
            setState("view");
            toast({
              title: "Phone number added",
              status: "success",
            });
          } catch (error) {
            if (error instanceof NeedReauthenticateError) {
              window.location.reload();
            } else {
              console.error(error);
              toast.genericError();
            }
          }
          break;
        }
        default: {
          const _exhaustiveCheck: never = values.step;
          throw new Error("Unhandled step");
        }
      }
    },
    [mfaApi, refreshFactors, toast]
  );

  const factorDictionary = React.useMemo(
    () => _.groupBy(factors, getPhoneFactorDeviceId),
    [factors]
  );

  const [isLoading, setIsLoading] = React.useState(false);
  const onManage = React.useCallback(async () => {
    setIsLoading(true);
    try {
      if (!(await mfaApi.hasMfaToken())) {
        window.location.reload();
        return;
      }
      onOpen();
      setIsLoading(false);
    } catch (error) {
      console.error(error);
      toast.genericError();
      setIsLoading(false);
    }
  }, [mfaApi, onOpen, toast]);

  return (
    <AdminCard
      icon={RiQuestionAnswerLine}
      variant="collapsible"
      collapse={!isOpen}
      title={
        <Flex gap="2" alignItems="center">
          Phone (SMS/Voice)
          <ConnectionTag isConnected={isActive} />
        </Flex>
      }
      description="Receive verification codes via SMS/Voice"
      actions={
        isOpen ? null : (
          <Button
            leftIcon={<RiShieldLine />}
            variant="outline"
            colorScheme="gray"
            isLoading={isLoading}
            loadingText="Manage"
            onClick={onManage}
          >
            Manage
          </Button>
        )
      }
    >
      {state === "view" ? (
        <>
          <Flex direction="column" gap="6">
            {Object.entries(factorDictionary).map(
              ([deviceId, phoneFactors]) => (
                <EnrolledPhone
                  key={deviceId}
                  phoneNumber={phoneFactors[0]?.name ?? ""}
                  factors={phoneFactors}
                  refreshFactors={refreshFactors}
                />
              )
            )}

            <Flex gap="4" justifyContent="space-between">
              {factors.length === 0 ? (
                <Flex direction="column" gap="1">
                  <Text>Phone authentication is not configured</Text>
                  <Text color="blackAlpha.600">
                    Add a phone number to receive verification codes by
                    SMS/voice.
                  </Text>
                </Flex>
              ) : null}

              <Spacer />
              <Button
                colorScheme="gray"
                leftIcon={<RiAddCircleLine />}
                onClick={() => setState("new")}
                size="sm"
                variant="outline"
              >
                Add phone number
              </Button>
            </Flex>
          </Flex>
          <AdminCardFormButtons>
            <Button size="sm" variant="ghost" onClick={onClose}>
              Close
            </Button>
          </AdminCardFormButtons>
        </>
      ) : null}

      {state === "new" ? (
        <Formik<FormType>
          initialValues={{
            step: "phone",
            phoneNumber: "",
            verificationCode: "",
            oobCode: "",
            recoveryCodes: [],
          }}
          validate={validateWithZod(FormikSchema)}
          onSubmit={onSubmit}
        >
          {(formik) => {
            const onCancel = () => {
              setState("view");
              formik.resetForm();
            };
            return (
              <>
                <Flex as={Form} direction="column" gap="4">
                  {formik.values.step === "phone" ? (
                    <PhoneStep onCancel={onCancel} />
                  ) : (
                    <CodeStep onCancel={onCancel} />
                  )}
                </Flex>
              </>
            );
          }}
        </Formik>
      ) : null}
    </AdminCard>
  );
};

const PhoneStep = (props: { onCancel: () => void }) => {
  const formik = useFormikContext<FormType>();
  return (
    <>
      <Text>
        Enter the mobile phone number that should receive your verification
        codes.
      </Text>
      <FormControl
        isInvalid={
          formik.touched.phoneNumber && formik.errors.phoneNumber !== undefined
        }
        maxWidth="24rem"
      >
        <FormLabel htmlFor="phoneNumber">Phone number</FormLabel>
        <PhoneInput
          id="phoneNumber"
          name="phoneNumber"
          autoComplete="tel"
          inputProps={{ placeholder: "(555) 555-5555" }}
        />
        <FormErrorMessage>{formik.errors.phoneNumber}</FormErrorMessage>
        <FormHelperText>Message and data rates may apply.</FormHelperText>
      </FormControl>
      <AdminCardFormButtons>
        <Button
          size="sm"
          variant="ghost"
          onClick={props.onCancel}
          isDisabled={formik.isSubmitting}
        >
          Cancel
        </Button>
        <Button
          size="sm"
          type="submit"
          isLoading={formik.isSubmitting}
          loadingText="Sending..."
        >
          Send verification code
        </Button>
      </AdminCardFormButtons>
    </>
  );
};

const CodeStep = (props: { onCancel: () => void }) => {
  const formik = useFormikContext<FormType>();
  return (
    <>
      <Flex direction="column" gap="1">
        <Text fontWeight="600">Enter your verification code</Text>
        <Text color="blackAlpha.600">
          We sent a six-digit code to{" "}
          {formatUSPhoneNumber(formik.values.phoneNumber)}
        </Text>
      </Flex>
      <InputControl
        name="verificationCode"
        label="Verification code"
        inputProps={{
          inputMode: "numeric",
          autoComplete: "one-time-code",
          maxLength: 6,
          pattern: "[0-9]*",
          placeholder: "6-digit code",
        }}
        maxWidth="20rem"
      />
      <Button
        alignSelf="flex-start"
        size="sm"
        variant="link"
        onClick={() => {
          formik.setValues((values) => ({
            ...values,
            step: "phone",
            verificationCode: "",
            recoveryCodes: [],
          }));
        }}
        isDisabled={formik.isSubmitting}
      >
        Use a different phone number
      </Button>
      <AdminCardFormButtons>
        <Button
          size="sm"
          variant="ghost"
          onClick={props.onCancel}
          isDisabled={formik.isSubmitting}
        >
          Cancel
        </Button>
        <Button
          size="sm"
          type="submit"
          isLoading={formik.isSubmitting}
          loadingText="Activating..."
        >
          Activate
        </Button>
      </AdminCardFormButtons>
    </>
  );
};

type EnrolledPhoneProps = {
  factors: Factor[];
  phoneNumber: string;
  refreshFactors: () => Promise<void>;
};

function getPhoneFactorDeviceId(factor: Factor): string {
  const separatorIndex = factor.id.indexOf("|");
  return separatorIndex === -1
    ? factor.id
    : factor.id.slice(separatorIndex + 1);
}

const EnrolledPhone = (props: EnrolledPhoneProps) => {
  const mfaApi = useMfaApi();
  const { factors, phoneNumber } = props;

  const channel = React.useMemo(() => {
    const hasSms = isNotNull(factors.find((i) => i.oob_channel === "sms"));
    const hasVoice = isNotNull(factors.find((i) => i.oob_channel === "voice"));

    if (hasSms && hasVoice) {
      return "SMS/Voice";
    } else if (hasVoice) {
      return "voice";
    } else {
      return "SMS";
    }
  }, [factors]);

  const confirmation = useConfirmationDialog({
    header: "Delete phone number?",
    body: `${maskedPhoneNumber(
      phoneNumber
    )} will no longer receive verification codes. You can add it again later.`,
    confirmButton: {
      label: "Yes, delete",
    },
  });
  const toast = useAvelaToast();

  const [isDeleting, setDeleting] = React.useState(false);
  const onDelete = React.useCallback(async () => {
    if (!(await confirmation.confirm())) {
      return;
    }

    // voice and sms is consider one factor, so we just need to delete 1 of them.
    const factor = factors[0];
    if (isNullOrUndefined(factor)) {
      return;
    }

    setDeleting(true);
    try {
      await mfaApi.deleteFactor(factor.id);
      await props.refreshFactors();
      setDeleting(false);
      toast({
        title: "Phone number deleted",
        status: "success",
      });
    } catch (error) {
      if (error instanceof NeedReauthenticateError) {
        window.location.reload();
      } else {
        console.error(error);
        setDeleting(false);
        toast.error({
          title: "Unable to delete phone",
        });
      }
    }
  }, [confirmation, factors, mfaApi, props, toast]);

  if (factors.length === 0) {
    return null;
  }

  return (
    <>
      <Flex direction="row" justifyContent="space-between">
        <Flex direction="column">
          <Text>{maskedPhoneNumber(phoneNumber)}</Text>
          <Text color="blackAlpha.600">
            Verification codes sent by {channel}
          </Text>
        </Flex>
        <Button
          leftIcon={<RiDeleteBin6Line />}
          size="sm"
          variant="outline"
          colorScheme="gray"
          isLoading={isDeleting}
          loadingText="Deleting..."
          onClick={onDelete}
        >
          Delete
        </Button>
      </Flex>
      {confirmation.confirmationDialog}
    </>
  );
};

const FormikSchema = z
  .object({
    step: z.union([z.literal("phone"), z.literal("code")]),
    phoneNumber: z.string(),
    verificationCode: z.string(),
    oobCode: z.string(),
    recoveryCodes: z.array(z.string()),
  })
  .superRefine((values, ctx) => {
    if (values.step === "phone") {
      if (values.phoneNumber.length === 0) {
        ctx.addIssue({
          code: "custom",
          message: "Please enter a phone number",
          path: ["phoneNumber"],
        });
      }
    } else {
      if (!/^\d{6}$/.test(values.verificationCode)) {
        ctx.addIssue({
          code: "custom",
          message: "Please enter a six-digit verification code",
          path: ["verificationCode"],
        });
      }
    }
  });

type FormType = z.infer<typeof FormikSchema>;
